<template>
  <b-card>
    <bank-transfer-card
      :payment="payment"
      class="mb-1"
    />

    <!-- Button: See uploaded document -->
    <b-button
      v-if="quote.payment_attachment"
      :variant="
        quote.payment_status === 'payment_rejected'
          ? 'outline-danger'
          : 'outline-secondary'
      "
      class="mb-75"
      block
      target="_blank"
      :href="quote.payment_attachment"
    >
      {{ $t('invoice') }}
    </b-button>

    <UploadPayment
      v-if="!paymentWindowExpired && hasTaxInformationLinked"
      ref="uploadPaymentRef"
      :quote="quote"
      @uploaded="handlePaymentStateChange"
      @removed="handlePaymentStateChange"
    />
    <b-button
      v-else-if="!paymentWindowExpired"
      variant="success"
      class="mb-75"
      block
      @click="openTaxInformationModal('upload')"
    >
      {{ $t('addPayment') }}
    </b-button>

    <b-alert
      v-else
      show
      variant="danger"
      class="mb-1"
    >
      No se puede subir comprobante: la cotización excedió 72 horas desde su creación.
    </b-alert>

    <b-button
      :to="{
        name: 'quoteInvoices',
        params: { quoteId: this.$route.params.quoteId },
      }"
      variant="outline-success"
      class="mb-75 mt-75"
      block
    >
      {{ $t('invoice2') }}
    </b-button>

    <b-button
      v-if="!quoteHasPaymentActivity"
      :variant="paymentWindowExpired ? 'primary' : 'outline-primary'"
      class="mb-75"
      block
      :disabled="isRequoting"
      @click="openRequoteConfirm('refresh')"
    >
      <b-spinner
        v-if="isRequoting && pendingRequoteMode === 'refresh'"
        small
        class="mr-50"
      />
      {{ $t('updateQuote') }}
    </b-button>
    <b-button
      variant="outline-primary"
      class="mb-75"
      block
      :disabled="isRequoting"
      @click="openRequoteConfirm('duplicate')"
    >
      <b-spinner
        v-if="isRequoting && pendingRequoteMode === 'duplicate'"
        small
        class="mr-50"
      />
      {{ $t('duplicateQuote') }}
    </b-button>

    <b-button
      variant="outline-secondary"
      class="mb-75"
      block
      @click="printInvoice"
    >
      {{ $t('print') }}
    </b-button>

    <b-alert
      v-if="paymentWindowExpired"
      show
      variant="danger"
      class="mb-1"
    >
      <div class="font-weight-bolder mb-25">
        Tu cotización venció
      </div>
      <div>
        Ya pasaron las 72 horas para pagar. Actualízala para obtener precios vigentes y volver a pagar; tu folio se mantiene.
      </div>
    </b-alert>
    <b-alert
      v-else-if="paymentDeadlineLabel && deadlineUpcoming"
      show
      :variant="deadlineVariant"
      class="mb-1"
    >
      <div>
        Puedes pagar hasta: <strong>{{ paymentDeadlineLabel }}</strong>
        <span v-if="deadlineUrgent"> — ¡queda poco tiempo!</span>
      </div>
    </b-alert>

    <b-button
      v-if="showWalletPaymentButton"
      variant="outline-info"
      class="mb-75"
      block
      :disabled="isProcessingWalletPayment || paymentWindowExpired"
      @click="openTaxInformationModal('wallet')"
    >
      {{ $t('payWithWallet') || 'Pagar con Monedero' }}
    </b-button>

    <!-- Button: Remission -->
    <b-button
      v-if="quote.quote_status === 'bid_approved' && (quote.is_paid || quote.payment_status === 'credit_approved')"
      :to="{
        name: 'remissions',
        params: { quoteId: this.$route.params.quoteId },
      }"
      variant="outline-success"
      class="mb-75"
      block
    >
      {{ $t('remission') }}
    </b-button>

    <PaymentsModal id="payments-modal" />

    <!-- MODAL CONFIRMACIÓN PAGO CON MONEDERO -->
    <confirmation-modal
      :id="'confirmWallet'"
      :variant="'info'"
      @confirm="payWithWalletConfirm"
    >
      <template v-slot:content>
        <div>
          <p class="mb-50">
            {{ $t('confirmWalletPayment') || '¿Confirmas pagar esta cotización con tu monedero?' }}
          </p>
          <ul class="mb-0">
            <li>
              {{ $t('quoteFolio') || 'Folio' }}:
              <strong>{{ quote.folio }}</strong>
            </li>

            <li>
              {{ $t('subtotal') || 'Subtotal' }}:
              <strong>{{ formatMoney(subtotal) }}</strong>
            </li>
            <li>
              {{ $t('shipping') || 'Flete' }}:
              <strong>{{ formatMoney(shippingTotal) }}</strong>
            </li>
            <li>
              {{ $t('vat') || 'IVA' }}:
              <strong>{{ formatMoney(ivaTotal) }}</strong>
            </li>
            <li>
              {{ $t('totalWithVAT') || 'Total con IVA' }}:
              <strong>{{ formatMoney(total) }}</strong>
            </li>

            <li v-if="paymentsSum > 0">
              {{ $t('alreadyPaid') || 'Pagado' }}:
              <strong>{{ formatMoney(paymentsSum) }}</strong>
            </li>
            <li>
              {{ $t('toCharge') || 'A cobrar' }}:
              <strong>{{ formatMoney(remainingToPay) }}</strong>
            </li>
            <li>
              {{ $t('walletBalance') || 'Saldo del monedero' }}:
              <strong>{{ formatMoney(walletBalance) }}</strong>
            </li>
          </ul>
        </div>
      </template>
    </confirmation-modal>

    <b-modal
      id="selectTaxInformationForPaymentUser"
      title="Selecciona razón social"
      ok-title="Continuar"
      cancel-title="Cancelar"
      @ok="confirmTaxInformationSelection"
      @hidden="resetTaxInformationModal"
    >
      <div v-if="taxInformationLoading">
        Cargando razones sociales...
      </div>
      <div v-else-if="taxInformationOptions.length === 0">
        <p class="mb-1">
          Debes registrar una razón social de facturación para poder continuar con el pago.
        </p>
        <b-button
          variant="primary"
          @click="goToNewBilling"
        >
          Registrar razón social
        </b-button>
      </div>
      <div v-else>
        <p class="mb-1">Selecciona la razón social que se asignará a esta cotización.</p>
        <b-form-select
          v-model="selectedTaxInformationId"
          :options="taxInformationSelectOptions"
        />
      </div>
    </b-modal>

    <confirmation-modal
      :id="'confirmRequote'"
      :variant="'warning'"
      @confirm="onRequoteQuote"
    >
      <template v-slot:content>
        <div>{{ requoteConfirmMessage }}</div>
      </template>
    </confirmation-modal>

  </b-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { computed, ref } from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import PaymentsModal from '@/components/payments/PaymentsModal.vue'
import UploadPayment from '@/components/payments/UploadPayment.vue'
import resolveQuoteRoute from '@/utils/quoteRoute'
import { quoteHasPaymentActivity as hasPaymentActivity } from '@/utils/quotePurchase'
import BankTransferCard from '@/components/invoice/BankTransferCard.vue'

export default {
  components: {
    BankTransferCard,
    ConfirmationModal,
    PaymentsModal,
    UploadPayment,
  },
  props: {
    quote: {
      type: Object,
      default() {
        return {}
      },
    },
    refetch: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      payment: {
        bankName: 'Santander',
        accountNumber: '65-50969732-6',
        clabe: '014580655096973264',
        card: '5579 0890 0447 7553',
        name: 'PLATAFORMA DIGITAL YOC SAPI DE CV',
        amount: 0,
        payment_attachment: {
          data: null,
        },
      },
    }
  },

  setup(props, { root }) {
    const store = root.$store
    const q = computed(() => props.quote || {})
    const quoteHasPaymentActivity = computed(() => hasPaymentActivity(q.value))
    const pendingRequoteMode = ref('refresh')
    const quoteId = computed(() => root.$route.params.quoteId || q.value?.id || null)
    const isRequoting = ref(false)
    const isProcessingWalletPayment = ref(false)

    // === mismos 2 clientes permitidos ===
    const WALLET_ALLOWED_CUSTOMERS = new Set([
      '9d391127-4675-444c-af7c-80d391251ec8',
      'b86f9e2a-e2af-4f35-a0f9-5c086e6704b4',
    ])

    // customerId real de la cotización con fallbacks
    const quoteCustomerId = computed(() => q.value?.customer_attributes?.id
      || q.value?.customer?.id
      || q.value?.customer_id
      || q.value?.customerId
      || null)
    const storeQuote = computed(() => store.state.quotes?.quote || {})
    const isSameQuoteInStore = computed(() => {
      const currentId = q.value?.id || quoteId.value
      const storeId = storeQuote.value?.id
      if (!currentId || !storeId) return false
      return String(storeId) === String(currentId)
    })
    const hasTaxInformationLinked = computed(() => Boolean(
      q.value?.tax_information_id
      || q.value?.taxable_id
      || q.value?.tax_information?.id
      || (isSameQuoteInStore.value && (
        storeQuote.value?.tax_information_id
        || storeQuote.value?.taxable_id
      )),
    ))

    // ✅ aquí NO metemos admin porque admin no usa este componente
    const allowWalletForThisQuote = computed(() => WALLET_ALLOWED_CUSTOMERS.has(String(quoteCustomerId.value || '')))

    // ---- Wallet / pagos (misma lógica que admin) ----
    const paymentWindowExpired = computed(() => Boolean(
      q.value?.customer_payment_window_expired ?? q.value?.payment_window_expired,
    ))

    const paymentDeadlineAt = computed(() => q.value?.customer_payment_deadline_at
      || q.value?.payment_deadline_at
      || null)

    const formatDeadline = value => {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return new Intl.DateTimeFormat('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d)
    }

    const paymentDeadlineLabel = computed(() => formatDeadline(paymentDeadlineAt.value))

    const hoursUntilDeadline = computed(() => {
      if (!paymentDeadlineAt.value) return null
      const d = new Date(paymentDeadlineAt.value)
      if (Number.isNaN(d.getTime())) return null
      return (d.getTime() - Date.now()) / (1000 * 60 * 60)
    })

    const deadlineUpcoming = computed(() => hoursUntilDeadline.value !== null && hoursUntilDeadline.value > 0)

    const deadlineUrgent = computed(() => deadlineUpcoming.value && hoursUntilDeadline.value <= 12)

    const deadlineVariant = computed(() => (deadlineUrgent.value ? 'warning' : 'info'))

    const requoteConfirmMessage = computed(() => (pendingRequoteMode.value === 'duplicate'
      ? 'Se creará una copia nueva de esta cotización. ¿Continuar?'
      : 'Se reenviará tu cotización a subasta para buscar precios actualizados. Tu folio se mantiene y tendrás una nueva ventana para pagar. ¿Continuar?'))

    const openRequoteConfirm = mode => {
      pendingRequoteMode.value = mode
      root.$bvModal.show('confirmRequote')
    }

    const walletBalance = computed(() => Number(store.state.wallet.walletMe?.data?.balance || 0))
    const taxInformationLoading = ref(false)
    const taxInformationOptions = ref([])
    const selectedTaxInformationId = ref(null)
    const pendingPaymentAction = ref(null)
    const uploadPaymentRef = ref(null)
    const taxInformationSelectOptions = computed(() => (taxInformationOptions.value || []).map(item => ({
      value: item.id,
      text: `${item.corporate_name} (${item.rfc})`,
    })))

    const payments = computed(() => store.getters['quotes/quotePayments'] || [])

    const subtotal = computed(() => Number(q.value.subtotal || 0))
    const shippingTotal = computed(() => Number(q.value.shipping_total || 0))
    const ivaTotal = computed(() => Number(q.value.iva_total || q.value.tax_total || 0))
    const total = computed(() => {
      const t = Number(q.value.total)
      if (!Number.isNaN(t) && t > 0) return t
      return subtotal.value + shippingTotal.value + ivaTotal.value
    })

    const paymentsSum = computed(() => payments.value.reduce((acc, p) => acc + Number(p.amount || 0), 0))

    const remainingToPay = computed(() => {
      const rest = Number(total.value) - Number(paymentsSum.value)
      return rest > 0 ? rest : 0
    })
    const showWalletPaymentButton = computed(() => (
      allowWalletForThisQuote.value
      && !q.value?.is_paid
      && Number(remainingToPay.value) > 0
    ))

    const formatMoney = n => {
      try {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
          .format(Number(n || 0))
      } catch {
        return `$${Number(n || 0).toFixed(2)}`
      }
    }

    function toast({ title, variant = 'success' }) {
      root.$toast({
        component: ToastificationContent,
        props: { title, icon: 'CreditCardIcon', variant },
      })
    }

    const getErrorPayload = error => error?.response?.data || error?.data || {}
    const getErrorMessage = (error, fallback = 'Ocurrió un error al procesar la solicitud.') => {
      const payload = getErrorPayload(error)
      if (Array.isArray(payload.messages) && payload.messages.length > 0) return payload.messages[0]
      if (typeof payload.error === 'string') return payload.error
      if (typeof error?.message === 'string') return error.message
      return fallback
    }
    const getErrorCode = error => getErrorPayload(error)?.code
    const openUploadDialog = () => {
      if (uploadPaymentRef.value && typeof uploadPaymentRef.value.openFileInput === 'function') {
        uploadPaymentRef.value.openFileInput()
        return
      }
      toast({ title: 'Razón social asignada. Ahora puedes subir el comprobante.', variant: 'success' })
    }

    async function onRequoteQuote() {
      try {
        isRequoting.value = true
        const sourceQuoteId = root.$route.params.quoteId || q.value?.id

        if (!sourceQuoteId) {
          throw new Error(root.$t('quoteNotFoundForRequote'))
        }

        const newQuote = await store.dispatch('quotes/requoteQuote', {
          quoteId: sourceQuoteId,
          mode: pendingRequoteMode.value,
        })

        root.$toast({
          component: ToastificationContent,
          props: {
            title: root.$t('quoteRequoted'),
            icon: 'CopyIcon',
            variant: 'success',
          },
        })

        if (String(newQuote.id) === String(sourceQuoteId)) {
          const targetRoute = resolveQuoteRoute({
            quote: newQuote,
            currentUser: store.getters['auth/currentUser'] || {},
            routeParams: root.$route.params,
          })
          if (targetRoute) {
            await root.$router.push(targetRoute).catch(() => {})
          } else {
            await store.dispatch('quotes/fetchQuote', sourceQuoteId)
          }
          return
        }

        await root.$router.push({
          name: 'addProductsToQuote',
          params: { quoteId: newQuote.id },
        })
      } catch (err) {
        const msg = err?.data?.messages?.[0] || err?.statusText || err?.message || root.$t('quoteRequoteFailed')
        root.$toast({
          component: ToastificationContent,
          props: {
            title: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isRequoting.value = false
      }
    }

    const loadWalletForQuote = async () => {
      // cliente solo puede cargar SU wallet
      await store.dispatch('wallet/fetchWalletMe')
    }

    const loadTaxInformationOptions = async () => {
      if (!quoteId.value) {
        toast({ title: 'No se encontró la cotización.', variant: 'danger' })
        return false
      }

      try {
        taxInformationLoading.value = true
        const response = await store.dispatch('quotes/fetchQuoteTaxInformationOptions', { quoteId: quoteId.value })
        taxInformationOptions.value = response?.data || []
        selectedTaxInformationId.value = response?.selected_tax_information_id
          || response?.project_default_tax_information_id
          || response?.customer_default_tax_information_id
          || taxInformationOptions.value?.[0]?.id
          || null
        return true
      } catch (error) {
        toast({ title: getErrorMessage(error, 'No se pudo cargar el catálogo fiscal.'), variant: 'danger' })
        return false
      } finally {
        taxInformationLoading.value = false
      }
    }

    const assignTaxInformationToQuote = async taxInformationId => {
      if (!quoteId.value || !taxInformationId) return false

      try {
        await store.dispatch('quotes/setQuoteTaxInformation', {
          quoteId: quoteId.value,
          taxInformationId,
        })
        await store.dispatch('quotes/fetchQuote', quoteId.value)
        return true
      } catch (error) {
        toast({ title: getErrorMessage(error, 'No fue posible asignar la razón social.'), variant: 'danger' })
        return false
      }
    }

    async function openTaxInformationModal(action) {
      if (action === 'wallet' && isProcessingWalletPayment.value) return

      if (hasTaxInformationLinked.value) {
        if (action === 'upload') {
          openUploadDialog()
          return
        }
        if (action === 'wallet') {
          try {
            await loadWalletForQuote()
            root.$bvModal.show('confirmWallet')
          } catch (error) {
            toast({ title: getErrorMessage(error, 'No se pudo cargar el monedero.'), variant: 'danger' })
          }
        }
        return
      }

      if (paymentWindowExpired.value) {
        toast({ title: 'No se puede pagar: la cotización excedió 72 horas desde su creación.', variant: 'warning' })
        return
      }
      pendingPaymentAction.value = action
      const loaded = await loadTaxInformationOptions()
      if (!loaded) return
      root.$bvModal.show('selectTaxInformationForPaymentUser')
    }

    async function confirmTaxInformationSelection(modalEvent) {
      if (!selectedTaxInformationId.value) {
        modalEvent.preventDefault()
        toast({ title: 'Selecciona una razón social para continuar.', variant: 'warning' })
        return
      }

      modalEvent.preventDefault()
      const assigned = await assignTaxInformationToQuote(selectedTaxInformationId.value)
      if (!assigned) return

      root.$bvModal.hide('selectTaxInformationForPaymentUser')

      if (pendingPaymentAction.value === 'upload') {
        await root.$nextTick()
        openUploadDialog()
        return
      }

      if (pendingPaymentAction.value === 'wallet') {
        try {
          await loadWalletForQuote()
          root.$bvModal.show('confirmWallet')
        } catch (error) {
          toast({ title: getErrorMessage(error, 'No se pudo cargar el monedero.'), variant: 'danger' })
        }
      }
    }

    function resetTaxInformationModal() {
      pendingPaymentAction.value = null
    }

    function goToNewBilling() {
      root.$bvModal.hide('selectTaxInformationForPaymentUser')
      root.$router.push({
        name: 'new-billing',
        params: quoteCustomerId.value ? { customerId: quoteCustomerId.value } : {},
      })
    }

    async function handlePaymentStateChange() {
      if (!quoteId.value) return

      try {
        const refreshedQuote = await store.dispatch('quotes/fetchQuote', quoteId.value)
        await store.dispatch('quotes/getQuotePayments', {
          quoteId: quoteId.value,
        })
        const targetRoute = resolveQuoteRoute({
          quote: refreshedQuote,
          currentUser: store.getters['auth/currentUser'] || {},
          routeParams: root.$route.params,
        })

        if (typeof props.refetch === 'function') {
          await props.refetch()
        }

        const resolvedTarget = targetRoute ? root.$router.resolve(targetRoute).route : null
        const targetFullPath = resolvedTarget?.fullPath

        if (targetFullPath && targetFullPath !== root.$route.fullPath) {
          window.location.replace(targetFullPath)
        }
      } catch (error) {
        toast({
          title: getErrorMessage(error, 'No se pudo refrescar la cotización después de guardar el comprobante.'),
          variant: 'danger',
        })
      }
    }

    async function payWithWalletConfirm() {
      if (isProcessingWalletPayment.value) return

      try {
        isProcessingWalletPayment.value = true

        if (paymentWindowExpired.value) {
          toast({ title: 'No se puede pagar: la cotización excedió 72 horas desde su creación.', variant: 'warning' })
          return
        }

        const wasCreditApproved = q.value?.payment_status === 'credit_approved'
        const projectId = q.value?.project_id

        if (Number(walletBalance.value) < Number(remainingToPay.value)) {
          toast({ title: (root.$t('insufficientWallet') || 'Saldo insuficiente'), variant: 'warning' })
          return
        }

        const amount = remainingToPay.value
        const idempotencyKey = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now())

        await store.dispatch('quotes/payWithWallet', {
          quoteId: quoteId.value,
          amount,
          idempotencyKey,
        })

        await loadWalletForQuote().catch(() => {})
        toast({ title: (root.$t('walletPaymentOk') || 'Pago con monedero realizado') })
        root.$bvModal.hide('confirmWallet')

        if (wasCreditApproved && projectId) {
          await root.$router.replace({ name: 'approvedQuotes', params: { projectId } })
          return
        }

        if (wasCreditApproved && !projectId) {
          toast({ title: 'No se encontró projectId para redirigir a Mis compras', variant: 'warning' })
        }

        await handlePaymentStateChange()
      } catch (err) {
        const code = getErrorCode(err)
        const msg = getErrorMessage(err)

        if (code === 'QUOTE_PAYMENT_WINDOW_EXPIRED') {
          toast({ title: msg || 'Cotización vencida', variant: 'warning' })
          return
        }

        toast({ title: msg, variant: 'danger' })
      } finally {
        isProcessingWalletPayment.value = false
      }
    }

    return {
      allowWalletForThisQuote,
      showWalletPaymentButton,
      paymentWindowExpired,
      openTaxInformationModal,
      payWithWalletConfirm,
      hasTaxInformationLinked,
      uploadPaymentRef,
      confirmTaxInformationSelection,
      resetTaxInformationModal,
      goToNewBilling,
      taxInformationLoading,
      taxInformationOptions,
      taxInformationSelectOptions,
      selectedTaxInformationId,
      walletBalance,
      paymentsSum,
      remainingToPay,
      subtotal,
      shippingTotal,
      ivaTotal,
      total,
      formatMoney,
      onRequoteQuote,
      isRequoting,
      quoteHasPaymentActivity,
      pendingRequoteMode,
      requoteConfirmMessage,
      openRequoteConfirm,
      paymentDeadlineLabel,
      deadlineUpcoming,
      deadlineUrgent,
      deadlineVariant,
      isProcessingWalletPayment,
      handlePaymentStateChange,
    }
  },

  computed: {
    ...mapState(['apiUrl']),
    ...mapGetters('quotes', ['quotePayments']),
  },

  methods: {
    printInvoice() {
      window.print()
    },
  },
}
</script>
